import { CaretDown } from "@phosphor-icons/react";
import { FC, ReactNode, useId, useRef, useState } from "react";

const SelectInput: FC<{
  placeholder?: string;
  disabled?: boolean;
  selected: string;
  data?: { id: string; value: string }[];
  onSelect: (id: string) => void;
  searchable?: boolean;
  triggerContent?: ReactNode;
  /** Replaces the default bordered trigger styling, e.g. inside a composite field. */
  triggerClassName?: string;
  onFocus?: () => void;
  onBlur?: () => void;
}> = ({ placeholder = "Select", disabled, data = [], selected, onSelect,
  searchable = data.length > 6, triggerContent, triggerClassName, onFocus, onBlur }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const listId = useId();
  const options = data.filter(option => option.value.toLowerCase().includes(query.trim().toLowerCase()));
  const open = isOpen && !disabled;
  const close = () => { setIsOpen(false); setQuery(""); };
  return (
    <div className="relative w-full text-[#4d4d4d]"
      onFocus={event => { if (!event.currentTarget.contains(event.relatedTarget)) onFocus?.(); }}
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget)) { close(); onBlur?.(); }
      }}
      onKeyDown={event => {
        if (!open) return;
        if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); close(); trigger.current?.focus(); }
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
          event.preventDefault();
          const buttons = Array.from(list.current?.querySelectorAll<HTMLButtonElement>("button") ?? []);
          const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
          const next = current < 0 ? (event.key === "ArrowDown" ? 0 : buttons.length - 1)
            : Math.max(0, Math.min(buttons.length - 1, current + (event.key === "ArrowDown" ? 1 : -1)));
          buttons[next]?.focus();
        }
        if (event.key === "Enter" && event.target instanceof HTMLInputElement) {
          event.preventDefault(); list.current?.querySelector<HTMLButtonElement>("button")?.click();
        }
      }}
    >
      <button ref={trigger} type="button" disabled={disabled} aria-label={placeholder}
        aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? listId : undefined}
        onClick={() => { if (open) close(); else { setQuery(""); setIsOpen(true); } }}
        className={triggerClassName ?? "flex items-center justify-between gap-2 rounded-lg text-left border border-gray-300 shadow w-full px-4 py-3 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"}
      >
        <div>
          {triggerContent ?? (data?.find((i) => i.id === selected)?.value ||
            placeholder ||
            "Select")}
        </div>
        <CaretDown weight="fill" />
      </button>
      {open && (
        <div className="absolute top-full mt-1 z-50 bg-white w-full min-w-[240px] max-w-[calc(100vw-3rem)] left-0 rounded-lg border border-gray-300 shadow-lg">
          {searchable && <input autoFocus value={query} aria-label={`Search ${placeholder.toLowerCase()}`}
            placeholder="Search..." onChange={event => setQuery(event.target.value)}
            className="block w-full border-b border-gray-200 rounded-t-lg px-3 py-2 outline-none focus:outline-none focus:ring-2 focus:ring-inset focus:ring-orange-500" />}
          <div ref={list} id={listId} role="listbox" aria-label={placeholder} className="max-h-52 overflow-y-auto overscroll-contain">
          {options.map((option) => {
            return (
              <button type="button" role="option" aria-selected={option.id === selected}
                key={option.id}
                onClick={(e) => {
                  onSelect(option.id);
                  close();
                  trigger.current?.focus();
                  e.stopPropagation();
                }}
                className={`block w-full text-left hover:bg-orange-50 focus:bg-orange-50 py-2 px-3 ${option.id === selected ? "font-semibold text-orange-600" : ""}`}
              >
                {option.value}
              </button>
            );
          })}
          {!options.length && <p role="status" className="p-3 text-sm text-gray-500">No options found.</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default SelectInput;
